import { LinearGradient } from 'expo-linear-gradient';
import { Image as RNImage, StyleSheet, Text, View } from 'react-native';
import { getRedesignScoreTier } from '../../../config/listingsConfig';
import { fontFamilies } from '../../../utils/fontLoader';
import ResponsiveImage from '../../ui/ResponsiveImage';

const SpecDot = () => <View style={styles.specDot} />;

const CarListingCard = ({ card, isMobile }) => {
  const cardWidth = isMobile ? '100%' : 375;

  return (
    <View style={[styles.wrapper, { width: cardWidth }]}>
      {/* Card top portion */}
      <LinearGradient
        colors={['rgba(255,255,255,0.04)', 'rgba(255,255,255,0.12)']}
        style={styles.card}
      >
        {/* Car image with badge */}
        <View style={styles.imageContainer}>
          {typeof card.image === 'string' ? (
            <RNImage
              source={{ uri: card.image }}
              style={styles.carImage}
              resizeMode="cover"
              accessibilityLabel={card.title}
            />
          ) : (
            <ResponsiveImage
              sources={card.image}
              widths={{
                small: 327,
                small2x: 654,
                medium: 375,
                medium2x: 750,
              }}
              sizes={isMobile ? '(max-width: 767px) 100vw, 375px' : '375px'}
              style={styles.carImage}
              resizeMode="cover"
              accessibilityLabel={card.title}
            />
          )}
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{card.badge}</Text>
          </View>
        </View>

        {/* Title */}
        <Text style={styles.title} numberOfLines={2}>{card.title}</Text>

        {/* Specs row */}
        <View style={styles.specsRow}>
          {card.specs.map((spec, i) => (
            <View key={spec} style={styles.specItem}>
              {i > 0 && <SpecDot />}
              <Text style={styles.specText}>{spec}</Text>
            </View>
          ))}
        </View>

        {/* Price */}
        <Text style={styles.price}>{card.price}</Text>

        {/* Match snippet */}
        {card.matchSnippet && (
          <Text style={styles.snippetText}>{card.matchSnippet}</Text>
        )}
      </LinearGradient>

      {/* Match bar — wider than card per Figma (407px vs 375px) */}
      <LinearGradient
        colors={['#303F61', '#1B2232']}
        style={[styles.matchBar, isMobile && styles.matchBarMobile]}
      >
        <Text style={styles.matchLabel}>Based on your profile</Text>
        <View style={[styles.matchBadge, { backgroundColor: getRedesignScoreTier(card.matchPercent).bgColor }]}>
          <Text style={[styles.matchPercent, { color: getRedesignScoreTier(card.matchPercent).color }]}>{card.matchPercent}% MATCH</Text>
        </View>
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    gap: 0,
    overflow: 'visible',
  },
  card: {
    borderWidth: 1,
    borderColor: 'rgba(225,231,242,0.08)',
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
    borderBottomWidth: 0,
    padding: 16,
    gap: 12,
  },
  imageContainer: {
    width: '100%',
    aspectRatio: 1.33,
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  carImage: {
    width: '100%',
    height: '100%',
  },
  badge: {
    position: 'absolute',
    top: 10,
    left: 10,
    backgroundColor: '#FF7B00',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeText: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 11,
    color: '#FFFFFF',
    letterSpacing: 0.5,
  },
  title: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 18,
    color: '#FFFFFF',
    lineHeight: 24,
  },
  specsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  specItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  specDot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: 'rgba(255,255,255,0.4)',
  },
  specText: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 12,
    color: 'rgba(255,255,255,0.7)',
  },
  price: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 20,
    color: '#FFFFFF',
  },
  snippetText: {
    fontFamily: fontFamilies.satoshi.medium,
    fontSize: 13,
    lineHeight: 13 * 1.4,
    color: 'rgba(255,255,255,0.7)',
  },
  matchBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    boxShadow: '0 -20px 80px rgba(0,0,0,0.4)',
    width: 407,
    alignSelf: 'center',
  },
  matchBarMobile: {
    width: '110%',
  },
  matchLabel: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 16,
    color: '#FFFFFF',
  },
  matchBadge: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderBottomRightRadius: 16,
    borderBottomLeftRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  matchPercent: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 14,
  },
});

export default CarListingCard;
